import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  HStack,
  Link,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { registerDevTool } from '../../registry';
import { client } from '@php-beam/packages/frontend/src/api/client';
import LeadSearch from '../ResourceComposer/LeadSearch';
import SetupBillingButton from './SetupBillingButton';
import TwoMinuteSection from './TwoMinuteSection';
import EmployeeSection from './EmployeeSection';
import GenericDocumentSection from './GenericDocumentSection';
import { ActiveLead, DocumentTag, LeadDoc, LeadQuoteInfo, LeadEmployeeInfo } from './types';

function DocumentSimulator() {
  const [tags, setTags] = useState<DocumentTag[]>([]);
  const [twoMinDefaults, setTwoMinDefaults] = useState<Record<string, any>>({});
  const [employeeDefaults, setEmployeeDefaults] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(true);
  const [activeLead, setActiveLead] = useState<ActiveLead | null>(null);
  const toast = useToast();

  useEffect(() => {
    client
      .get<{ document_tags: DocumentTag[]; two_minute_defaults: Record<string, any>; employee_defaults: Record<string, any> }>(
        '/devtools/documents/defaults',
      )
      .then((res) => {
        setTags(res.data.document_tags);
        setTwoMinDefaults(res.data.two_minute_defaults);
        setEmployeeDefaults(res.data.employee_defaults);
      })
      .catch(() => toast({ title: 'Failed to load document defaults', status: 'error' }))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Text p={4}>Loading...</Text>;

  return (
    <VStack align="stretch" spacing={4}>
      <Box>
        <Text fontSize="xs" fontWeight="semibold" color="gray.500" mb={1}>
          Select lead
        </Text>
        <LeadSearch
          onSelect={(lead) =>
            setActiveLead({ id: lead.id, business_name: lead.business_name, url: lead.url })
          }
        />
      </Box>

      {activeLead && (
        <Alert status="success" borderRadius="md">
          <AlertIcon />
          <Text fontSize="sm">
            <Link href={activeLead.url} fontWeight="bold" color="purple.600">
              {activeLead.business_name}
            </Link>{' '}
            (ID: {activeLead.id})
          </Text>
          <Button
            size="xs"
            ml="auto"
            variant="ghost"
            onClick={() => setActiveLead(null)}
          >
            Clear
          </Button>
        </Alert>
      )}

      {activeLead && (
        <LeadDocumentPanel
          lead={activeLead}
          tags={tags}
          twoMinDefaults={twoMinDefaults}
          employeeDefaults={employeeDefaults}
        />
      )}
    </VStack>
  );
}

function LeadDocumentPanel({
  lead,
  tags,
  twoMinDefaults,
  employeeDefaults,
}: {
  lead: ActiveLead;
  tags: DocumentTag[];
  twoMinDefaults: Record<string, any>;
  employeeDefaults: Record<string, any>;
}) {
  const [docs, setDocs] = useState<LeadDoc[]>([]);
  const [quotes, setQuotes] = useState<LeadQuoteInfo[]>([]);
  const [employees, setEmployees] = useState<LeadEmployeeInfo[]>([]);
  const [hasBilling, setHasBilling] = useState(false);
  const [requiredForSubmission, setRequiredForSubmission] = useState<string[]>([]);
  const [loadingDocs, setLoadingDocs] = useState(true);

  const refresh = useCallback(() => {
    setLoadingDocs(true);
    client
      .get<{
        documents: LeadDoc[];
        quotes: LeadQuoteInfo[];
        employees: LeadEmployeeInfo[];
        has_billing: boolean;
        required_for_submission: string[];
      }>(`/devtools/documents/lead/${lead.id}`)
      .then((res) => {
        setDocs(res.data.documents);
        setQuotes(res.data.quotes);
        setEmployees(res.data.employees);
        setHasBilling(res.data.has_billing);
        setRequiredForSubmission(res.data.required_for_submission);
      })
      .finally(() => setLoadingDocs(false));
  }, [lead.id]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <VStack align="stretch" spacing={4}>
      <HStack>
        <Text fontWeight="semibold" fontSize="sm">
          Status
        </Text>
        <Badge colorScheme={hasBilling ? 'green' : 'gray'} fontSize="xs">
          {hasBilling ? 'Billing OK' : 'No Billing'}
        </Badge>
        <Badge colorScheme={quotes.length > 0 ? 'green' : 'gray'} fontSize="xs">
          {quotes.length} quote(s)
        </Badge>
        <Badge colorScheme={docs.length > 0 ? 'green' : 'gray'} fontSize="xs">
          {docs.length} doc(s)
        </Badge>
        <Badge colorScheme={employees.length > 0 ? 'green' : 'gray'} fontSize="xs">
          {employees.length} employee(s)
        </Badge>
      </HStack>

      {!hasBilling && (
        <SetupBillingButton leadId={lead.id} onDone={refresh} />
      )}

      <TwoMinuteSection
        leadId={lead.id}
        quotes={quotes}
        defaults={twoMinDefaults}
        onDone={refresh}
      />

      <EmployeeSection
        leadId={lead.id}
        employees={employees}
        defaults={employeeDefaults}
        onDone={refresh}
      />

      <GenericDocumentSection
        leadId={lead.id}
        tags={tags}
        existingDocs={docs}
        requiredForSubmission={requiredForSubmission}
        onDone={refresh}
      />

      {docs.length > 0 && (
        <Box>
          <Text fontSize="xs" fontWeight="semibold" color="gray.500" mb={1}>
            Documents on lead
          </Text>
          {docs.map((doc) => (
            <HStack key={doc.id} fontSize="xs" py={0.5}>
              <Badge fontSize="2xs">{doc.tag}</Badge>
              <Text>{doc.name}</Text>
            </HStack>
          ))}
        </Box>
      )}
    </VStack>
  );
}

registerDevTool({
  name: 'Documents',
  component: DocumentSimulator,
});
